import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode
} from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { clearUserId, trackEvent } from '../utils/googleAnalytics';
import { resetBillingPromptDismissal } from '../utils/billingPromptDismissal';
import { resetRecoveryCodesPromptDismissal } from '../utils/recoveryCodesPrompt';
import { clearRecoveryCodeSignIn } from '../utils/recoveryCodes';

interface AuthContextType {
  isLoggedIn: boolean;
  // True once the initial session check against the httpOnly cookie has settled.
  // Optional to consume — rendering does not block on it — but available for any
  // surface that wants to wait before deciding logged-in vs logged-out.
  authResolved: boolean;
  // Call after a successful login / 2FA validation. The access token now lives in
  // an httpOnly cookie the server set, so there is nothing for JS to store — this
  // just records that we hold a session.
  markSignedIn: () => void;
  redirectUrl: string | null;
  saveRedirectUrl: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const API_URL = process.env.REACT_APP_API_URL;

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  // Phase 2: the access token is an httpOnly cookie JS can't read, so "logged in"
  // is no longer "do we hold a token". Start optimistically from persisted (non-
  // sensitive) user data so a reload doesn't flash the login page, then confirm
  // against the server below.
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(
    () => !!localStorage.getItem('incrypt_app_userData')
  );
  const [authResolved, setAuthResolved] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);

  // Confirm the session cookie once on load. On a definitive 401 (even after a
  // refresh attempt) we drop the optimistic session; a network blip leaves it
  // untouched so nobody is logged out over a hiccup. Individual API calls still
  // self-heal via the network layer's 401 → refresh → logout.
  useEffect(() => {
    // Nothing persisted ⇒ treat as logged out without pinging the server. Avoids
    // two auth requests on every anonymous page view (the public marketing site).
    // A session established in this tab goes through markSignedIn(), not here.
    if (!localStorage.getItem('incrypt_app_userData')) {
      setAuthResolved(true);
      return;
    }

    let cancelled = false;

    const confirmSession = async (): Promise<boolean | null> => {
      try {
        await axios.get(`${API_URL}/auth/me`, { withCredentials: true });
        return true;
      } catch (error) {
        if (!axios.isAxiosError(error) || error.response?.status !== 401) {
          return null; // unknown (network/server) — keep the optimistic value
        }
        try {
          await axios.post(
            `${API_URL}/auth/refresh-token`,
            {},
            { withCredentials: true }
          );
          await axios.get(`${API_URL}/auth/me`, { withCredentials: true });
          return true;
        } catch {
          return false;
        }
      }
    };

    confirmSession().then((result) => {
      if (cancelled) return;
      if (result === true) {
        setIsLoggedIn(true);
      } else if (result === false) {
        setIsLoggedIn(false);
        localStorage.removeItem('incrypt_app_userData');
      }
      setAuthResolved(true);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const markSignedIn = useCallback(() => {
    setIsLoggedIn(true);
    /* A new sign-in gets asked again about moving their billing off the old Stripe
     * account: "Not now" is answered per sign-in, not once and for all (see
     * utils/billingPromptDismissal). Cleared here as well as on logout, because a
     * session can end by expiring rather than by anyone pressing Log out. */
    resetBillingPromptDismissal();
    // and the save-your-recovery-codes prompt is answered per sign-in the same way
    resetRecoveryCodesPromptDismissal();
    /* Likewise "you signed in with a recovery code" belongs to the sign-in that used
     * one: the 2FA step records it again, after this call, when the server says so. */
    clearRecoveryCodeSignIn();
  }, []);

  const logout = useCallback(() => {
    // Clear the httpOnly cookies server-side (JS can't reach them) via the public
    // logout endpoint — public so it still works when the access token has already
    // expired (a session-authed logout would 401 and leave the refresh cookie
    // behind). Best-effort and fire-and-forget: local state is always cleared, so
    // a failed/offline request can never trap the user in a session.
    axios
      .post(`${API_URL}/auth/logout`, {}, { withCredentials: true })
      .catch(() => {
        /* ignore: logging out must never be blocked by a failed request */
      });

    setIsLoggedIn(false);
    localStorage.removeItem('incrypt_app_userData');
    localStorage.removeItem('incrypt_app_redirectUrl');
    resetBillingPromptDismissal();
    resetRecoveryCodesPromptDismissal();
    clearRecoveryCodeSignIn();
    clearUserId();
    trackEvent('logout');
  }, []);

  const saveRedirectUrl = useCallback(() => {
    setRedirectUrl(location.pathname);
  }, [location.pathname]);

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn,
        authResolved,
        markSignedIn,
        logout,
        redirectUrl,
        saveRedirectUrl
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
